import React from 'react';
import { Typography, Box, Chip } from '@mui/material';

 
const ymdFromAnything = (v) => {
  if (!v) return null;
  const s = String(v).trim();
  const m = s.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
};

const prettyFromYMD = (ymd) => {
  if (!ymd) return '';
  const parts = ymd.split('-');
  if (parts.length !== 3) return ymd;
  const [yy, mm, dd] = parts;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const mi = Math.max(1, Math.min(12, parseInt(mm, 10))) - 1;
  const di = parseInt(dd, 10);
  return `${months[mi]} ${isNaN(di) ? dd : di}, ${yy}`;
};

 
function formatAddress(address) {
  if (!address) return '';
  const {
    addressName, companyName, streetAddress, suiteApt,
    optional1, optional2, city, stateProvince, country, postalZipCode,
  } = address;
  return [addressName, companyName, streetAddress, suiteApt, optional1, optional2, city, stateProvince, country, postalZipCode]
    .filter(Boolean)
    .join(', ');
}

const coerceToDbAddressShape = (raw) => {
  if (!raw) return null;
  if (raw.streetAddress || raw.city || raw.stateProvince || raw.postalZipCode || raw.addressName || raw.companyName) return raw;
  const { street, city, state, postal, country, name } = raw || {};
  if (street || city || state || postal || country || name) {
    return {
      addressName: name || undefined,
      streetAddress: street || undefined,
      city: city || undefined,
      stateProvince: state || undefined,
      postalZipCode: postal || undefined,
      country: country || undefined,
    };
  }
  return null;
};

 
const hasValue = (v) => {
  if (v === 0) return true;
  return String(v ?? '').trim() !== '';
};

const ACCOUNT_TYPE_LABELS = {
  supplier: 'Supplier',
  our: 'Our',
  client: 'Client',
  other: 'Other',
};

const detectCarrier = (text) => {
  if (!text) return null;
  const value = String(text);
  if (/ups/i.test(value)) return 'UPS';
  if (/fedex/i.test(value)) return 'FedEx';
  if (/canada\s*post|canadapost|post\s*canada|cp\b/i.test(value)) return 'Canada Post';
  if (/dhl/i.test(value)) return 'DHL';
  if (/usps|postal/i.test(value)) return 'USPS';
  return null;
};

const CARRIER_LOGOS = {
  UPS: 'https://elite-core-dev.s3.us-west-2.amazonaws.com/customer-files/2c5b94fb-cf88-40df-b43d-f1d404fe7b09.png',
  FedEx: 'https://elite-core-dev.s3.us-west-2.amazonaws.com/customer-files/0fa430ef-d605-4643-ba7c-625f8d8ce981.png',
  'Canada Post': 'https://elite-core-dev.s3.us-west-2.amazonaws.com/customer-files/78dd0518-cd9e-4b4d-add7-4d68d4c188c9.png',
};

const formatShippingAccount = (account) => {
  if (!account) return null;
  if (typeof account === 'string') {
    const normalized = String(account).trim().toLowerCase();
    if (ACCOUNT_TYPE_LABELS[normalized]) return null;
    return { typeLabel: ACCOUNT_TYPE_LABELS[account] || account, detail: null, carrier: detectCarrier(account) };
  }
  if (String(account.Type || '').toLowerCase() === 'other') {
    const manualValue = account.AccountValue || account.AccountType || 'Other';
    return { typeLabel: manualValue, detail: null, carrier: detectCarrier(manualValue) };
  }
  const typeLabel = ACCOUNT_TYPE_LABELS[account.Type] || account.Type || '';
  const detailParts = [
    account.AccountName, account.AccountType, account.AccountValue,
    account.AccountNumber ? `#${account.AccountNumber}` : null,
    account.AccountId ? `ID ${account.AccountId}` : null,
  ].filter(Boolean);
  const detail = detailParts.join(' • ') || null;
  const carrier = account.Courier || detectCarrier(account.AccountName || account.AccountType || account.AccountValue);
  if (!detail && !carrier) return null;
  return { typeLabel, detail, carrier };
};

 
// const LABEL_WIDTH = 130; // px 
const LABEL_WIDTH = 150;
 
const FieldRow = ({ label, value, highlightWhenMissing = true, children }) => {
  const showChip = highlightWhenMissing && !hasValue(value) && !children;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `${LABEL_WIDTH}px 1fr`,
        gap: '0 8px',
        alignItems: 'start',
        mb: 1.25,
      }}
    >
      {/* Label */}
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: 'text.secondary',
          lineHeight: 1.6,
          whiteSpace: 'nowrap',
          pt: '1px',
        }}
      >
        {label}
      </Typography>

      {/* Value */}
      <Box sx={{ minWidth: 0 }}>
        {showChip ? (
          <Chip
            label="Value needed"
            color="warning"
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        ) : children ? (
          children
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              lineHeight: 1.65,
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
          >
            {value}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

 
const OptionalRow = ({ label, value, children }) => {
  if (!hasValue(value) && !children) return null;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `${LABEL_WIDTH}px 1fr`,
        gap: '0 8px',
        alignItems: 'start',
        mb: 1.25,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: 'text.secondary',
          lineHeight: 1.6,
          whiteSpace: 'nowrap',
          pt: '1px',
        }}
      >
        {label}
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        {children ? (
          children
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              lineHeight: 1.65,
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
          >
            {value}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
 
const ShippingInfo = ({ type, data, customerAddressOptions = [] }) => {
  const isDecorator = String(type).toLowerCase() === 'decorator';

  // Name
  const displayName = isDecorator ? 'Ship to Client' : (data?.SupplierName ?? '');
 
  const inhandsYMD = ymdFromAnything(data?.InhandsDate);
  const displayDate = inhandsYMD ? prettyFromYMD(inhandsYMD) : '';
  const isFirm = Boolean(data?.IsFirm ?? data?.ShipTo?.IsFirm);
  const hasInHandsValue = Boolean(displayDate || isFirm);

  // Ship To label
  const shipToTypeRaw = isDecorator
    ? (data?.ShipToType ?? data?.Type ?? data?.ShipTo?.Type ?? data?.ShipTo ?? '')
    : data?.ShipTo;
  const isShipToClient = /client/i.test(String(shipToTypeRaw));
  const displayShipTo = isDecorator
    ? (isShipToClient ? 'Client Address' : (data?.ShipTo || shipToTypeRaw || ''))
    : (data?.ShipTo ?? '');

  // Address text
  let addressText = '';
  if (isDecorator) {
    const clientRaw = data?.ClientRawAddress || data?.ShipTo?.ClientRawAddress || null;
    const clientId  = data?.ClientAddressId  || data?.ShipTo?.ClientAddressId  || null;
    const coerced = coerceToDbAddressShape(clientRaw);
    if (coerced && isShipToClient) {
      addressText = formatAddress(coerced);
    } else if (clientId != null && isShipToClient) {
      const match = customerAddressOptions.find(a => String(a.id) === String(clientId));
      addressText = match ? formatAddress(match) : `Address #${clientId}`;
    } else if (data?.ClientAddress && isShipToClient) {
      addressText = formatAddress(data.ClientAddress);
    } else {
      addressText = formatAddress(data?.Address);
    }
  } else {
    addressText = formatAddress(data?.Address);
  }

  // Fade when empty
  const allEmpty = [displayName, displayDate, displayShipTo, addressText]
    .every(val => String(val || '').trim() === '');

  // Shipping account
  const accountInfo = formatShippingAccount(data?.ShippingAccount);
  const accountDisplay = accountInfo
    ? [accountInfo.typeLabel, accountInfo.detail].filter(Boolean).join(' • ')
    : '';
  const carrierLogo = accountInfo?.carrier ? CARRIER_LOGOS[accountInfo.carrier] : null;

  return (
    <Box sx={{ opacity: isDecorator && allEmpty ? 0.3 : 1, transition: 'opacity 0.2s' }}>
      {/* Name */}
      <FieldRow label="Name:" value={displayName} />

      {/* In Hands Date */}
      <FieldRow label="In Hands Date:" highlightWhenMissing={!hasInHandsValue} value="">
        {hasInHandsValue ? (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
            {displayDate && (
              <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.65 }}>
                {displayDate}
              </Typography>
            )}
            {isFirm && (
              <Chip
                label="Firm"
                color="error"
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            )}
          </Box>
        ) : (
          <Chip
            label="Value needed"
            color="warning"
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        )}
      </FieldRow>

      {/* Ship To */}
      <FieldRow label="Ship To:" value={displayShipTo} />

      {/* Address long text wraps and indents under the value column */}
      <FieldRow label="Address:" value={addressText} />

      {/* Shipping Account */}
      {accountInfo?.typeLabel && (
        <FieldRow label="Shipping Account:" highlightWhenMissing={false} value="">
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{ color: 'text.primary', lineHeight: 1.65, wordBreak: 'break-word', overflowWrap: 'anywhere' }}
            >
              {accountDisplay}
            </Typography>
            {accountInfo.carrier && (
              carrierLogo ? (
                <Box
                  component="img"
                  src={carrierLogo}
                  alt={`${accountInfo.carrier} logo`}
                  sx={{ height: 22, width: 'auto', maxWidth: 60, flexShrink: 0 }}
                />
              ) : (
                <Chip
                  label={accountInfo.carrier}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              )
            )}
          </Box>
        </FieldRow>
      )}

      {/* Supplier / Decorator Notes */}
      <OptionalRow
        label={isDecorator ? 'Decorator Notes:' : 'Supplier Notes:'}
        value={isDecorator ? data?.DecoratorNotes : data?.SupplierNotes}
      />
    </Box>
  );
};

export default ShippingInfo;
